import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {Model} from '#lit/components/base/weekday_selector/architecture.js';
import {Msg, main} from '#lit/components/base/weekday_selector/architecture.js';
import {classMap} from 'lit/directives/class-map.js';
import {repeat} from 'lit/directives/repeat.js';
import type {Weekday, WeekdayDescription} from '#lit/common/constants/dates.js';
import {isString} from '#lit/utils/index.js';

import styles from './style.scss';

export const WEEKDAYS_SELECTOR_ELEMENT_TAG = 'fly-weekday-selector';

@customElement(WEEKDAYS_SELECTOR_ELEMENT_TAG)
export default class WeekdaySelector extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get formAssociated() {
    return true;
  }

  static get styles() {
    return [styles];
  }

  get internals() {
    return this._internals;
  }

  @property({type: String, reflect: true})
  form?: string;

  @property({type: Boolean, reflect: true})
  required = false;

  @property({type: String, reflect: true})
  name?: string;

  @property({type: Boolean, reflect: true})
  get disabled() {
    return this.model.disabled;
  }
  set disabled(value: boolean) {
    this.handleUpdate(value ? Msg.disable() : Msg.enable());
  }

  @property({type: Array})
  get value(): Weekday[] {
    return this.model.valueAsArray;
  }
  set value(value: Weekday[]) {
    this.setValue(isString(value) ? JSON.parse(value) : value);
  }

  @property({type: Boolean, reflect: true})
  get onlyWorkdays(): boolean {
    return !this.model.onlyWorkdays;
  }
  set onlyWorkdays(value: boolean) {
    this.handleUpdate(Msg.setOnlyWorkdays(value ?? false));
  }

  @property({type: Boolean, reflect: true})
  get multiSelect() {
    return this.model.multiSelection;
  }
  set multiSelect(value: boolean) {
    this.handleUpdate(Msg.allowMultiSelection(value));
  }

  private _internals = this.attachInternals();

  constructor() {
    super();
    this.updateFormValue();
  }

  private isDisabled(weekdayValue: WeekdayDescription['value']): boolean {
    return (
      this.onlyWorkdays &&
      Object.keys(this.model.days.weekend).includes(String(weekdayValue))
    );
  }

  updateFormValue() {
    this.internals.setFormValue(this.value.join(','));
  }

  setValue(value: Weekday[]) {
    this.handleUpdate(Msg.setValue(value));
  }

  selectWeekday(weekday: Weekday) {
    this.handleUpdate(Msg.selectWeekday(weekday));
  }

  deselectWeekday(weekday: Weekday) {
    // must always have at least one day selected
    if (this.model.valueAsArray.length === 1) return;
    this.handleUpdate(Msg.deselectWeekday(weekday));
  }

  renderWeekdayButton(weekday: WeekdayDescription): unknown {
    const individuallyDisabled = this.isDisabled(weekday.value);
    const disabled = individuallyDisabled || this.model.disabled;
    const selected =
      !individuallyDisabled && this.model.value.has(weekday.value);
    return html`
      <fly-tooltip content=${weekday.longName}>
        <button
          type="button"
          aria-label=${weekday.longName}
          ?disabled=${disabled}
          class=${classMap({
            'workday-btn': true,
            selected,
          })}
          @click=${() => {
            if (this.model.disabled) return;
            if (this.model.multiSelection) {
              return selected
                ? this.deselectWeekday(weekday.value)
                : this.selectWeekday(weekday.value);
            }
            return this.handleUpdate(Msg.setSingleValue(weekday.value));
          }}
        >
          <p class="name short-name">${weekday.narrowName}</p>
        </button>
      </fly-tooltip>
    `;
  }

  render() {
    return html`
      ${repeat(
        this.model.days.all,
        weekday => weekday.value,
        weekday => this.renderWeekdayButton(weekday)
      )}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [WEEKDAYS_SELECTOR_ELEMENT_TAG]: WeekdaySelector;
  }
}
